/**
 * Acquiring and releasing capture sources, and finding out where we stand with
 * the browser *before* asking for any of them.
 *
 * The display stream is the expensive one: getting it costs the user a
 * permission dialog and a source picker. Everything in the recorder is built
 * around holding onto it: restart tears down the MediaRecorder and never the
 * stream, which is the single behaviour that made us abandon Cap.
 *
 * The microphone and the camera are cheap to hold but expensive to ask for
 * badly. A prompt nobody triggered is denied far more often than one they did,
 * and a denial is sticky: it survives reloads and hides behind a padlock icon
 * most people have never clicked. So this module exposes the *query* side of
 * permissions as well as the request side, and the recorder only ever calls
 * getUserMedia either from a real gesture or when the answer is already yes.
 */

export type SourceKind = 'display' | 'mic' | 'camera';

/**
 * Why an acquisition failed, in terms somebody can act on rather than in
 * DOMException names.
 *
 *   denied       the browser, or the person, said no.
 *   missing      there is no such device on this machine.
 *   busy         something else holds the device open. A call, usually.
 *   unsupported  the device exists but will not do what was asked of it.
 *   unknown      everything else, including a picker that was dismissed.
 */
export type DenialReason = 'denied' | 'missing' | 'busy' | 'unsupported' | 'unknown';

export class CaptureDenied extends Error {
  readonly kind: SourceKind;
  readonly reason: DenialReason;

  constructor(kind: SourceKind, cause: unknown) {
    const reason = reasonFor(cause);
    super(messageFor(kind, reason));
    this.name = 'CaptureDenied';
    this.kind = kind;
    this.reason = reason;
    this.cause = cause;
  }
}

/**
 * Media errors arrive as DOMException in most browsers and as a bare
 * OverconstrainedError in some, so only the name is worth reading.
 */
function errorName(cause: unknown): string {
  if (cause instanceof DOMException || cause instanceof Error) return cause.name;
  if (typeof cause === 'object' && cause !== null) {
    const name = (cause as { name?: unknown }).name;
    if (typeof name === 'string') return name;
  }
  return '';
}

function reasonFor(cause: unknown): DenialReason {
  switch (errorName(cause)) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'missing';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'busy';
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'unsupported';
    default:
      return 'unknown';
  }
}

function messageFor(kind: SourceKind, reason: DenialReason): string {
  const label = kind === 'display' ? 'screen' : kind === 'mic' ? 'microphone' : 'camera';
  // Naming the control is the difference between a dead end and a ten second
  // fix, and the two browsers put it in two different places.
  const control = kind === 'camera' ? 'camera icon' : 'padlock icon';
  const setting = kind === 'camera' ? 'Camera' : 'Microphone';

  switch (reason) {
    case 'denied':
      return kind === 'display'
        ? 'Screen sharing was cancelled. Pick a screen, window, or tab to start recording.'
        : `Your browser is blocking the ${label}. Click the ${control} in the address bar, set ${setting} to Allow, then try again.`;
    case 'missing':
      return `No ${label} was found on this device.`;
    case 'busy':
      return `Another app is already using your ${label}. Close it, a video call is the usual culprit, then try again.`;
    case 'unsupported':
      return `Your ${label} does not support the settings Slate asked for.`;
    default:
      return `Could not start the ${label}.`;
  }
}

// ---------------------------------------------------------------------------
// Permissions, without asking
// ---------------------------------------------------------------------------

/** The two inputs whose permission we care about ahead of time. */
export type MediaPermissionKind = 'microphone' | 'camera';

/**
 * Where we stand with the browser. `prompt` is also the honest answer whenever
 * we cannot find out, which is most of Safari: not knowing and not yet having
 * asked lead to exactly the same behaviour, so they are the same state here.
 */
export type MediaPermissionState = 'granted' | 'prompt' | 'denied';

function normalise(state: PermissionState | undefined): MediaPermissionState {
  return state === 'granted' || state === 'denied' ? state : 'prompt';
}

/**
 * `microphone` and `camera` are real permission names in Chromium and Firefox,
 * but they are not in the TypeScript `PermissionName` union, and Safari has
 * historically thrown a TypeError for both. Every one of those failures is the
 * same non-answer, so they all resolve to null and the caller degrades.
 */
async function queryStatus(kind: MediaPermissionKind): Promise<PermissionStatus | null> {
  if (typeof navigator === 'undefined' || typeof navigator.permissions?.query !== 'function') {
    return null;
  }
  try {
    return await navigator.permissions.query({ name: kind } as unknown as PermissionDescriptor);
  } catch {
    return null;
  }
}

/** Read the current state. Never shows a dialog, never throws. */
export async function capturePermission(kind: MediaPermissionKind): Promise<MediaPermissionState> {
  const status = await queryStatus(kind);
  return normalise(status?.state);
}

/**
 * Read the current state and keep reading it.
 *
 * The interesting case is somebody granting the microphone from the browser's
 * own padlock menu while the recorder is open: `PermissionStatus` fires
 * `change`, the recorder brings the source up, and nobody has to reload the
 * page to be heard. Where the API does not exist the callback simply never
 * fires and the caller stays on its `prompt` default.
 *
 * Returns an unsubscribe function that is safe to call before the query has
 * even settled.
 */
export function watchCapturePermission(
  kind: MediaPermissionKind,
  onChange: (state: MediaPermissionState) => void,
): () => void {
  let status: PermissionStatus | null = null;
  let cancelled = false;
  const handle = (): void => {
    if (status && !cancelled) onChange(normalise(status.state));
  };

  void queryStatus(kind).then((result) => {
    if (cancelled || !result) return;
    status = result;
    onChange(normalise(result.state));
    result.addEventListener('change', handle);
  });

  return () => {
    cancelled = true;
    status?.removeEventListener('change', handle);
  };
}

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

const DISPLAY_CONSTRAINTS: DisplayMediaStreamOptions = {
  video: {
    frameRate: { ideal: 30, max: 30 },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
  // Tab and (on Chromium) system audio. Harmless where unsupported: the
  // browser simply returns a stream with no audio track.
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  },
};

export async function requestDisplay(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getDisplayMedia(DISPLAY_CONSTRAINTS);
  } catch (error) {
    throw new CaptureDenied('display', error);
  }
}

/**
 * One getUserMedia call, with one retry.
 *
 * A remembered deviceId is a guess about hardware, and hardware gets unplugged.
 * `deviceId: { exact: ... }` does not fall back on its own: it fails, which
 * would strand somebody whose headset is in another bag. So when a *specific*
 * device was asked for and the browser says there is no such thing, ask again
 * for whatever the system calls default. Every other failure, and any failure
 * where no device was named, is reported exactly as it happened.
 */
async function acquire(
  kind: SourceKind,
  deviceId: string | null,
  constraints: (id: string | null) => MediaStreamConstraints,
): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia(constraints(deviceId));
  } catch (error) {
    const reason = reasonFor(error);
    if (deviceId !== null && (reason === 'missing' || reason === 'unsupported')) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints(null));
      } catch (fallbackError) {
        throw new CaptureDenied(kind, fallbackError);
      }
    }
    throw new CaptureDenied(kind, error);
  }
}

export function requestMic(deviceId?: string | null): Promise<MediaStream> {
  return acquire('mic', deviceId ?? null, (id) => ({
    audio: {
      ...(id ? { deviceId: { exact: id } } : {}),
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  }));
}

export function requestCamera(deviceId?: string | null): Promise<MediaStream> {
  return acquire('camera', deviceId ?? null, (id) => ({
    video: {
      ...(id ? { deviceId: { exact: id } } : {}),
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30 },
      facingMode: 'user',
    },
  }));
}

export function stopStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      // Already stopped; nothing to do.
    }
  }
}

export interface InputDevice {
  deviceId: string;
  label: string;
}

/**
 * Device labels are blank until a permission has been granted at least once,
 * so this is worth calling again after the first successful getUserMedia.
 */
export async function listDevices(kind: 'audioinput' | 'videoinput'): Promise<InputDevice[]> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') return [];
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === kind)
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `${kind === 'audioinput' ? 'Microphone' : 'Camera'} ${index + 1}`,
      }));
  } catch {
    return [];
  }
}

/** Track dimensions, falling back to 1080p when the browser will not say. */
export function trackSize(track: MediaStreamTrack | undefined): { width: number; height: number } {
  const settings = track?.getSettings();
  return {
    width: settings?.width && settings.width > 0 ? settings.width : 1920,
    height: settings?.height && settings.height > 0 ? settings.height : 1080,
  };
}
